'use client'

import { ArrowLeft, Loader2, Sparkles } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import type { ImportableStop } from '@/lib/import/types'
import { clientErrorMessage } from './extract-error'

interface PasteStepProps {
	/**
	 * Fired with the extracted stops and the source key once the paste resolves
	 * to at least one stop. `sourceKey` keys the per-source imported-tracker.
	 */
	onStops: (stops: ImportableStop[], sourceKey: string) => void
	/** Returns to the source picker. */
	onBack: () => void
}

/** Source key for pasted routes — keys the imported-tracker for this branch. */
const PASTE_SOURCE_KEY = 'paste'

/** Generic, user-facing failure copy when the extract call itself fails. */
const EXTRACT_ERROR = 'Could not read that — please try again.'

/** Outcome of an extract POST: stops to commit, a user-facing error, or ignore. */
type ExtractResult =
	| { kind: 'ok'; stops: ImportableStop[] }
	| { kind: 'error'; message?: string }
	| { kind: 'ignore' }

/**
 * POST the pasted text to the shared extract route and normalize the outcome.
 * Pulling the branchy fetch/guard logic out of the component keeps the submit
 * handler well under the complexity ceiling. Returns `ignore` when superseded
 * or aborted so the caller commits nothing.
 */
async function extractFromText(text: string, signal: AbortSignal): Promise<ExtractResult> {
	try {
		const res = await fetch('/api/import/extract', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ text }),
			signal,
		})
		if (!res.ok) {
			return { kind: 'error', message: (await clientErrorMessage(res)) ?? undefined }
		}
		const data = (await res.json()) as { stops?: ImportableStop[] }
		return { kind: 'ok', stops: data.stops ?? [] }
	} catch (err) {
		if (err instanceof DOMException && err.name === 'AbortError') {
			return { kind: 'ignore' }
		}
		return { kind: 'error' }
	}
}

/**
 * Step 'paste' of the import wizard: turn pasted itinerary text into stops.
 *
 * Renders a textarea, an "Extract stops" action (disabled while empty, inline
 * spinner while posting), and a Back affordance. On submit it POSTs the text to
 * the shared `/api/import/extract` route; an empty result is surfaced as a
 * non-blocking toast (the text was readable, it just named no stops), a network
 * failure as an inline error with the action staying available to retry. A
 * sequence + abort guard prevents an out-of-order or double-submit commit.
 */
export function PasteStep({ onStops, onBack }: PasteStepProps) {
	const [text, setText] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Each submit claims the next number; only the latest may commit. The abort
	// controller cancels an in-flight request superseded by a newer submit.
	const seqRef = useRef(0)
	const abortRef = useRef<AbortController | null>(null)

	const trimmed = text.trim()
	const canSubmit = trimmed.length > 0 && !submitting

	const handleSubmit = useCallback(async () => {
		if (trimmed.length === 0) {
			return
		}

		abortRef.current?.abort()
		const controller = new AbortController()
		abortRef.current = controller
		const seq = ++seqRef.current

		setSubmitting(true)
		setError(null)

		const result = await extractFromText(trimmed, controller.signal)
		// A newer submit superseded this one while it was in flight — ignore.
		if (seq !== seqRef.current || result.kind === 'ignore') {
			return
		}

		if (result.kind === 'error') {
			setError(result.message ?? EXTRACT_ERROR)
		} else if (result.stops.length === 0) {
			toast.error('No stops found — check the format')
		} else {
			onStops(result.stops, PASTE_SOURCE_KEY)
		}
		setSubmitting(false)
	}, [trimmed, onStops])

	return (
		<div className="flex flex-col gap-3">
			<textarea
				value={text}
				onChange={(event) => setText(event.target.value)}
				rows={8}
				placeholder="Paste your route or itinerary here…"
				className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none transition-colors placeholder:text-text-muted focus-visible:border-primary"
			/>
			<p className="text-xs text-text-muted">
				One stop per line — e.g. &ldquo;Jul 8 St. Paul, MN&rdquo;
			</p>

			{error && (
				<p aria-live="polite" className="text-sm text-error-fg">
					{error}
				</p>
			)}

			<div className="flex items-center justify-between gap-2">
				<Button type="button" variant="ghost" size="sm" onClick={onBack} disabled={submitting}>
					<ArrowLeft className="size-4" aria-hidden />
					Back
				</Button>
				<Button type="button" variant="default" onClick={handleSubmit} disabled={!canSubmit}>
					{submitting ? (
						<>
							<Loader2 className="size-4 animate-spin" aria-hidden />
							Extracting…
						</>
					) : (
						<>
							<Sparkles className="size-4" aria-hidden />
							Extract stops
						</>
					)}
				</Button>
			</div>
		</div>
	)
}
